/**
 * Customer home.
 *
 * Ordered by what a retailer actually needs to act on:
 *
 *   1. Deliveries awaiting their confirmation — these auto-confirm in 48 hours,
 *      so the window is short and the prompt has to be unmissable.
 *   2. What they owe.
 *   3. Orders in flight.
 *   4. A shortcut back into ordering.
 */

import { useNavigate } from 'react-router-dom';
import { Page } from '../../components/Shell';
import { Greeting } from '../../components/Greeting';
import {
  Banner, Button, Card, Chip, Money, Skeleton, Tile,
} from '../../components/ui';
import { useQuery, useRefreshOnFocus } from '../../lib/useApi';
import { useAuth } from '../../lib/auth';
import { useCart } from '../../lib/cart';
import { useT } from '../../lib/i18n';
import { dateShort, hoursUntil, money, relative } from '../../lib/format';
import { IconChevron } from '../../components/Icons';

interface Ledger {
  customer: { name: string; code: string; creditLimitPaise: number; discountPercent: number };
  outstandingPaise: number;
  creditAvailablePaise: number | null;
  ageing: Record<string, number>;
  pendingVerification: { amountPaise: number; count: number; note: string };
  returnablePackaging: { returnable_type: string; quantity_outstanding: number }[];
}

interface PendingDelivery {
  id: string;
  delivered_at: string;
  driver_declared_quantity: number;
  auto_confirm_due_at: string;
  invoice_number: string;
  shop_name: string;
  driver_name: string;
}

interface OrderRow {
  id: string;
  order_number: string;
  order_date: string;
  status: string;
  net_amount_paise: number;
  invoice_number: string | null;
  payment_status: string | null;
  balance_amount_paise: number | null;
  shop_name: string;
}

export default function Home() {
  const t = useT();
  const navigate = useNavigate();
  const { user } = useAuth();
  const cart = useCart();

  const ledger = useQuery<Ledger>(
    user?.customerId ? `/api/invoices/ledger/${user.customerId}` : null,
    { cacheKey: 'me:ledger', ttlSeconds: 300 },
  );
  const pending = useQuery<{ items: PendingDelivery[]; count: number }>('/api/deliveries/pending-confirmation');
  const orders = useQuery<{ items: OrderRow[] }>('/api/orders?pageSize=5');

  useRefreshOnFocus(() => {
    void ledger.refetch();
    void pending.refetch();
    void orders.refetch();
  });

  const activeOrders = orders.data?.items.filter(
    (o) => !['delivered', 'closed', 'cancelled'].includes(o.status),
  ) ?? [];

  return (
    <Page
      title={ledger.data?.customer.name ?? t('app.name')}
      subtitle={ledger.data?.customer.code}
      badges={{ cart: cart.totalQuantity }}
    >
      <div className="stack">
        <Greeting />
        {/* Highest priority: the 48-hour confirmation window is short, and an
            unconfirmed delivery is the retailer's only chance to flag a
            shortfall before it auto-confirms. */}
        {pending.data && pending.data.count > 0 && (
          <Card>
            <div className="stack-2">
              <div className="row-between">
                <h3>{t('delivery.confirmTitle')}</h3>
                <Chip tone="warn">{pending.data.count}</Chip>
              </div>
              {pending.data.items.slice(0, 3).map((d) => {
                const hrs = hoursUntil(d.auto_confirm_due_at);
                return (
                  <button
                    key={d.id}
                    className="list__item"
                    style={{ borderRadius: 10, border: '1px solid var(--line)' }}
                    onClick={() => navigate(`/deliveries/${d.id}/confirm`)}
                  >
                    <div className="grow">
                      <div className="t-semi t-sm">{d.invoice_number}</div>
                      <div className="t-xs t-muted">
                        {t('delivery.driverSaid', { n: d.driver_declared_quantity })} · {relative(d.delivered_at)}
                      </div>
                      {hrs > 0 && (
                        <div className="t-xs t-warn">
                          {t('delivery.autoConfirmIn', { hours: hrs })}
                        </div>
                      )}
                    </div>
                    <IconChevron width={18} height={18} />
                  </button>
                );
              })}
            </div>
          </Card>
        )}

        {ledger.loading && !ledger.data && <Skeleton rows={2} height={88} />}

        {ledger.data && (
          <>
            <div className="grid-2">
              <Tile
                label={t('ledger.outstanding')}
                value={money(ledger.data.outstandingPaise)}
                tone={ledger.data.outstandingPaise > 0 ? 'warn' : undefined}
                meta={
                  ledger.data.creditAvailablePaise !== null
                    ? `${t('ledger.available')} ${money(ledger.data.creditAvailablePaise, { compact: true })}`
                    : undefined
                }
              />
              <Tile
                label={t('ledger.creditLimit')}
                value={money(ledger.data.customer.creditLimitPaise, { compact: true })}
                meta={
                  ledger.data.customer.discountPercent > 0
                    ? `${ledger.data.customer.discountPercent}% discount`
                    : undefined
                }
              />
            </div>

            {/* Money paid but not yet approved. Shown separately and labelled,
                because it has NOT reduced the outstanding balance — conflating
                the two is how a retailer thinks they have paid when the ledger
                disagrees. */}
            {ledger.data.pendingVerification.count > 0 && (
              <Banner tone="info" title={t('ledger.underVerification')}>
                <span className="t-sm">
                  {money(ledger.data.pendingVerification.amountPaise)} · {t('ledger.underVerificationHint')}
                </span>
              </Banner>
            )}

            {ledger.data.outstandingPaise > 0 && (
              <Button block onClick={() => navigate('/ledger')}>{t('ledger.payNow')}</Button>
            )}

            {ledger.data.returnablePackaging.length > 0 && (
              <Card>
                <div className="row-between">
                  <span className="t-sm t-muted">{t('ledger.crates')}</span>
                  <span className="t-semi">
                    {ledger.data.returnablePackaging
                      .map((c) => `${c.quantity_outstanding} ${c.returnable_type}`)
                      .join(' · ')}
                  </span>
                </div>
              </Card>
            )}
          </>
        )}

        {activeOrders.length > 0 && (
          <Card flush>
            <div className="card__header">
              <h3>{t('order.title')}</h3>
              <Button variant="quiet" size="sm" onClick={() => navigate('/orders')}>
                {t('app.total')}
              </Button>
            </div>
            <div className="list">
              {activeOrders.map((o) => (
                <button key={o.id} className="list__item" onClick={() => navigate(`/orders/${o.id}`)}>
                  <div className="grow">
                    <div className="row" style={{ gap: 8 }}>
                      <span className="t-semi t-sm">{o.order_number}</span>
                      <Chip tone={statusTone(o.status)}>{t(`order.${o.status}`)}</Chip>
                    </div>
                    <div className="t-xs t-muted">{dateShort(o.order_date)}</div>
                  </div>
                  <Money paise={o.net_amount_paise} className="t-sm" />
                  <IconChevron width={18} height={18} style={{ color: 'var(--faint)' }} />
                </button>
              ))}
            </div>
          </Card>
        )}

        <Button variant="ghost" block size="lg" onClick={() => navigate('/catalogue')}>
          {t('nav.catalogue')}
        </Button>
      </div>
    </Page>
  );
}

export function statusTone(status: string): 'neutral' | 'ok' | 'warn' | 'danger' | 'info' {
  switch (status) {
    case 'delivered':
    case 'closed': return 'ok';
    case 'cancelled': return 'danger';
    case 'out_for_delivery':
    case 'dispatched': return 'info';
    case 'partially_delivered': return 'warn';
    default: return 'neutral';
  }
}
